import { Link } from 'react-router-dom';
import { Mail, Linkedin, MapPin } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kober-energy/artifacts/vys7c9cr_3.png";
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

const footerLinks = [
  { label: 'About Us',          id: 'about',     route: null },
  { label: 'Renewable Energy',  id: 'renewable', route: null },
  { label: 'Project Financing', id: 'financing', route: null },
  { label: 'BESS Gigafactory',  id: 'bess',      route: null },
  { label: 'Projects',          id: 'projects',  route: null },
  { label: 'The Holy Grail',       id: null,        route: '/holy-grail' },
  { label: 'C&I Energy Solutions', id: null,        route: null, href: 'https://koberenergy.com/',    external: true },
  { label: 'BESS Integrator',      id: null,        route: null, href: 'https://bessintegrator.com/', external: true },
  { label: 'TRANSFORMERS',         id: null,        route: null, href: '/gp-transformers#configurator', hash: true },
  { label: 'News',                 id: 'news',      route: null },
];

const socialLinks = [
  { icon: Linkedin, href: 'https://www.linkedin.com/in/k%C3%B6ber-energy-holding-a67725407/', label: 'LinkedIn' },
  { icon: Mail,     href: 'mailto:office@koberholding.com',                                        label: 'Email' },
];

const legalLinks = [
  { label: 'Sustainability', to: '/sustainability', testid: 'footer-legal-sustainability' },
  { label: 'Privacy Policy', to: '/privacy',        testid: 'footer-legal-privacy-policy' },
  { label: 'Terms of Use',   to: '/terms',           testid: 'footer-legal-terms-of-use' },
  { label: 'Cookie Policy',  to: '/cookies',         testid: 'footer-legal-cookie-policy' },
];

function FooterBrand({ tagline }) {
  return (
    <div className="lg:col-span-4">
      <img src={LOGO_URL} alt="KÖBER Energy Holding" className="h-40 w-auto mb-6" />
      <p className="text-sm text-white/45 leading-relaxed max-w-xs mb-6">{tagline}</p>
      <div className="flex gap-3">
        {socialLinks.map(({ icon: Icon, href, label }) => (
          <a
            key={label}
            href={href}
            aria-label={label}
            target={href.startsWith('http') ? '_blank' : undefined}
            rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
            data-testid={`footer-social-${label.toLowerCase()}`}
            className="w-9 h-9 border border-white/10 flex items-center justify-center text-white/35 hover:border-[#D4A017]/50 hover:text-[#D4A017] transition-all duration-200"
          >
            <Icon size={14} />
          </a>
        ))}
      </div>
    </div>
  );
}

function FooterLinkItem({ link }) {
  const cls = "text-sm text-[#D4A017]/60 hover:text-[#D4A017] transition-colors duration-200";
  const testId = `footer-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  if (link.external) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={cls}>
        {link.label}
      </a>
    );
  }
  if (link.hash) {
    return (
      <a href={link.href} data-testid={testId} className={cls}>
        {link.label}
      </a>
    );
  }
  if (link.route) {
    return (
      <Link to={link.route} data-testid={testId} className={cls}>
        {link.label}
      </Link>
    );
  }
  return (
    <button onClick={() => scrollTo(link.id)} data-testid={`footer-link-${link.id}`} className="text-sm text-white/40 hover:text-[#D4A017] transition-colors duration-200 text-left">
      {link.label}
    </button>
  );
}

function FooterLinks({ heading }) {
  return (
    <div className="lg:col-span-3">
      <h4 className="text-sm font-medium text-white mb-5 tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>{heading}</h4>
      <ul className="space-y-3">
        {footerLinks.map((link) => (
          <li key={link.label}>
            <FooterLinkItem link={link} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function FooterContact({ onContact }) {
  return (
    <div className="lg:col-span-5">
      <h4 className="text-sm font-medium text-white mb-5 tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>Contact</h4>
      <ul className="space-y-3 mb-6">
        <li className="flex items-start gap-2.5 text-sm text-white/40">
          <MapPin size={13} className="mt-0.5 flex-shrink-0" />
          <span>18 Cluceru Udricani Street, 030782, 3rd District, Bucharest, Romania</span>
        </li>
        <li className="flex items-center gap-2.5 text-sm text-white/40">
          <Mail size={13} className="flex-shrink-0" />
          <a href="mailto:office@koberholding.com" className="hover:text-[#D4A017] transition-colors">office@koberholding.com</a>
        </li>
      </ul>
      <button
        data-testid="footer-contact-btn"
        onClick={onContact}
        className="border border-[#D4A017]/50 text-[#D4A017] text-sm px-6 py-2.5 hover:bg-[#D4A017] hover:text-black transition-all duration-200"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        Get in Touch
      </button>
    </div>
  );
}

function FooterLegal({ copyright }) {
  return (
    <div className="pt-8 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-4">
      <p className="text-xs text-white/25">{copyright}</p>
      <div className="flex gap-6">
        {legalLinks.map(({ label, to, testid }) => (
          <Link key={label} to={to} data-testid={testid} className="text-xs text-white/25 hover:text-white/55 transition-colors duration-200">
            {label}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Footer({ text, onContact }) {
  return (
    <footer className="bg-[#050505] border-t border-white/8">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl pt-16 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
          <FooterBrand tagline={text.tagline} />
          <FooterLinks heading={text.links.company} />
          <FooterContact onContact={onContact} />
        </div>
        <FooterLegal copyright={text.copyright} />
      </div>
    </footer>
  );
}
